export enum LogLevel {
    DEBUG = 'debug',
    INFO = 'info',
    WARN = 'warn',
    ERROR = 'error',
    METRIC = 'metric'
}

export enum LogCategory {
    TRANSCRIPT_UI = 'TRANSCRIPT_UI',
    TRANSCRIPT_API = 'TRANSCRIPT_API',
    TRANSCRIPT_PERFORMANCE = 'TRANSCRIPT_PERFORMANCE',
    TRANSCRIPT_USER_ACTION = 'TRANSCRIPT_USER_ACTION',
    TRANSCRIPT_ERROR = 'TRANSCRIPT_ERROR'
}

interface LogContext {
    userId?: string;
    sessionId?: string;
    recordingId?: string;
    meetingId?: string;
    timestamp: string;
    environment: string;
    version: string;
}

interface LogPayload {
    level: LogLevel;
    category: LogCategory;
    action: string;
    message: string;
    context: LogContext;
    data?: Record<string, any>;
    error?: Error;
    performance?: PerformanceMetrics;
}

interface PerformanceMetrics {
    duration?: number;
    startTime?: number;
    endTime?: number;
    memoryUsage?: number;
    renderTime?: number;
}

class TranscriptLogger {
    private static instance: TranscriptLogger;
    private context: LogContext;
    private buffer: LogPayload[] = [];
    private flushInterval: number = 5000; // 5 seconds
    private intervalId?: number;
    
    private constructor() {
        this.context = this.initializeContext();
        this.startBufferFlush();
    }
    
    static getInstance(): TranscriptLogger {
        if (!TranscriptLogger.instance) {
            TranscriptLogger.instance = new TranscriptLogger();
        }
        return TranscriptLogger.instance;
    }
    
    private initializeContext(): LogContext {
        return {
            userId: this.getUserId(),
            sessionId: this.getSessionId(),
            timestamp: new Date().toISOString(),
            environment: import.meta.env.MODE || 'development',
            version: import.meta.env.VITE_APP_VERSION || '1.0.0'
        };
    }
    
    private getUserId(): string {
        try {
            return localStorage.getItem('userId') || 'anonymous';
        } catch {
            return 'anonymous';
        }
    }
    
    private getSessionId(): string {
        try {
            let sessionId = sessionStorage.getItem('sessionId');
            if (!sessionId) {
                sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                sessionStorage.setItem('sessionId', sessionId);
            }
            return sessionId;
        } catch {
            return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
    }
    
    private startBufferFlush() {
        this.intervalId = window.setInterval(() => {
            if (this.buffer.length > 0) {
                this.flush();
            }
        }, this.flushInterval);
    }
    
    private flush() {
        const logs = [...this.buffer];
        this.buffer = [];
        
        // Send to remote logging service in production
        if (import.meta.env.PROD) {
            this.sendToRemote(logs);
        }
        
        // Always log to console in development
        if (import.meta.env.DEV) {
            logs.forEach(log => this.logToConsole(log));
        }
    }
    
    private logToConsole(payload: LogPayload) {
        const style = this.getConsoleStyle(payload.level);
        console.log(
            `%c[${payload.level.toUpperCase()}] ${payload.category} - ${payload.action}`,
            style,
            {
                message: payload.message,
                data: payload.data,
                context: payload.context,
                performance: payload.performance
            }
        );
        
        if (payload.error) {
            console.error(payload.error);
        }
    }
    
    private getConsoleStyle(level: LogLevel): string {
        const styles = {
            [LogLevel.DEBUG]: 'color: #6b7280; font-weight: normal;',
            [LogLevel.INFO]: 'color: #0ea5e9; font-weight: normal;',
            [LogLevel.WARN]: 'color: #f59e0b; font-weight: bold;',
            [LogLevel.ERROR]: 'color: #ef4444; font-weight: bold;',
            [LogLevel.METRIC]: 'color: #10b981; font-weight: normal;'
        };
        return styles[level];
    }
    
    private async sendToRemote(logs: LogPayload[]) {
        try {
            // In a real implementation, you would send to your logging endpoint
            // For now, we'll just store in localStorage as a fallback
            const existingLogs = localStorage.getItem('transcript_logs');
            const allLogs = existingLogs ? JSON.parse(existingLogs) : [];
            allLogs.push(...logs);
            
            // Keep only last 1000 logs to prevent storage overflow
            if (allLogs.length > 1000) {
                allLogs.splice(0, allLogs.length - 1000);
            }
            
            localStorage.setItem('transcript_logs', JSON.stringify(allLogs));
        } catch (error) {
            console.error('Failed to send logs to remote:', error);
        }
    }
    
    // Public logging methods
    debug(category: LogCategory, action: string, message: string, data?: any) {
        this.log(LogLevel.DEBUG, category, action, message, data);
    }
    
    info(category: LogCategory, action: string, message: string, data?: any) {
        this.log(LogLevel.INFO, category, action, message, data);
    }
    
    warn(category: LogCategory, action: string, message: string, data?: any) {
        this.log(LogLevel.WARN, category, action, message, data);
    }
    
    error(category: LogCategory, action: string, message: string, error?: Error, data?: any) {
        this.log(LogLevel.ERROR, category, action, message, data, error);
    }
    
    metric(category: LogCategory, action: string, metrics: PerformanceMetrics, data?: any) {
        this.log(LogLevel.METRIC, category, action, 'Performance metric', data, undefined, metrics);
    }
    
    private log(
        level: LogLevel,
        category: LogCategory,
        action: string,
        message: string,
        data?: any,
        error?: Error,
        performance?: PerformanceMetrics
    ) {
        const payload: LogPayload = {
            level,
            category,
            action,
            message,
            context: {
                ...this.context,
                timestamp: new Date().toISOString()
            },
            data: this.sanitizeLogData(data),
            error,
            performance
        };
        
        this.buffer.push(payload);
        
        // Immediate flush for errors
        if (level === LogLevel.ERROR) {
            this.flush();
        }
    }
    
    // Sanitize sensitive data before logging
    private sanitizeLogData(data: any): any {
        if (!data || typeof data !== 'object') {
            return data;
        }
        
        const sanitized = { ...data };
        
        // Remove or mask sensitive fields
        if (sanitized.email) {
            sanitized.email = sanitized.email.replace(/(.{2})(.*)(@.*)/, '$1***$3');
        }
        
        if (sanitized.signedUrl) {
            sanitized.signedUrl = '[REDACTED]';
        }
        
        if (sanitized.transcriptContent) {
            sanitized.transcriptContent = '[CONTENT_REDACTED]';
        }
        
        return sanitized;
    }
    
    // Utility method for timing operations
    async measurePerformance<T>(
        category: LogCategory,
        action: string,
        operation: () => Promise<T>,
        data?: any
    ): Promise<T> {
        const startTime = performance.now();
        
        try {
            const result = await operation();
            const duration = performance.now() - startTime;
            
            this.metric(category, action, {
                duration,
                startTime,
                endTime: performance.now()
            }, data);
            
            return result;
        } catch (error) {
            const duration = performance.now() - startTime;
            
            this.error(
                category,
                action,
                `Operation failed after ${duration}ms`,
                error as Error,
                { ...data, duration }
            );
            
            throw error;
        }
    }
    
    // Clean up resources
    destroy() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        this.flush(); // Final flush
    }
}

export const logger = TranscriptLogger.getInstance();